import React, { useState } from 'react';
import { Landmark, User, Lock, Eye, EyeOff, ArrowRight, Check } from 'lucide-react';
interface LoginPageProps {
  onLogin: () => void;
}
const features = ['Seamless application management', 'Real-time portfolio tracking', 'Secure multi-role access control'];
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const handleDemo = (role: 'Maker' | 'Checker') => {
    setUserId(role === 'Maker' ? 'maker.demo' : 'checker.demo');
    setPassword('password123');
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userId && password) {
      onLogin();
    }
  };
  return <div className="min-h-screen w-full bg-gray-100 flex items-center justify-center p-4 sm:p-6" data-id="element-771">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 bg-white rounded-2xl shadow-xl overflow-hidden" data-id="element-772">
        {/* LEFT — Brand panel */}
        <div className="relative hidden lg:flex flex-col justify-center p-12 bg-slate-900 text-white overflow-hidden" data-id="element-773">
          {/* subtle grid */}
          <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: 'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} aria-hidden="true" data-id="element-774" />
          <div className="relative" data-id="element-775">
            {/* Brand mark */}
            <div className="flex flex-col items-center mb-12" data-id="element-776">
              <div className="h-12 w-12 rounded-xl bg-slate-800 flex items-center justify-center mb-2" data-id="element-777">
                <Landmark className="h-6 w-6 text-emerald-400" data-id="element-778" />
              </div>
              <span className="text-sm font-semibold" data-id="element-779">
                Bunyaad <span className="text-emerald-400" data-id="element-780">Finance</span>
              </span>
            </div>

            <h1 className="text-4xl font-bold tracking-tight" data-id="element-781">
              Bunyaad <span className="text-emerald-400" data-id="element-782">Finance</span>
            </h1>
            <p className="mt-2 text-sm font-medium text-emerald-400 uppercase tracking-wider" data-id="element-783">
              Loan Management System
            </p>
            <p className="mt-5 text-sm leading-relaxed text-slate-300 max-w-sm" data-id="element-784">
              Streamline your lending operations with a{' '}
              <span className="font-semibold text-white" data-id="element-785">
                secure, integrated platform
              </span>{' '}
              — enabling fast and transparent credit decisions.
            </p>

            <ul className="mt-10 space-y-4" data-id="element-786">
              {features.map(feature => <li key={feature} className="flex items-center text-sm text-slate-200" data-id="element-787">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/20 mr-3 shrink-0" data-id="element-788">
                    <Check className="h-3.5 w-3.5 text-emerald-400" data-id="element-789" />
                  </span>
                  {feature}
                </li>)}
            </ul>
          </div>
        </div>

        {/* RIGHT — Form panel */}
        <div className="flex flex-col justify-center p-8 sm:p-12" data-id="element-790">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900" data-id="element-791">
            Welcome back
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-792">
            Sign in to access your account.
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit} data-id="element-793">
            <div data-id="element-794">
              <label htmlFor="userId" className="block text-sm font-semibold text-gray-900 mb-1.5" data-id="element-795">
                User ID
              </label>
              <div className="relative rounded-lg shadow-sm" data-id="element-796">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-797">
                  <User className="h-4 w-4 text-gray-400" aria-hidden="true" data-id="element-798" />
                </div>
                <input id="userId" type="text" required value={userId} onChange={e => setUserId(e.target.value)} className="block w-full rounded-lg border border-gray-300 pl-10 pr-3 py-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" placeholder="Enter your user ID" data-id="element-799" />
              </div>
            </div>

            <div data-id="element-800">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-1.5" data-id="element-801">
                Password
              </label>
              <div className="relative rounded-lg shadow-sm" data-id="element-802">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-803">
                  <Lock className="h-4 w-4 text-gray-400" aria-hidden="true" data-id="element-804" />
                </div>
                <input id="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)} className="block w-full rounded-lg border border-gray-300 pl-10 pr-10 py-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" placeholder="Enter your password" data-id="element-805" />
                <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-806">
                  {showPassword ? <EyeOff className="h-4 w-4" data-id="element-807" /> : <Eye className="h-4 w-4" data-id="element-808" />}
                </button>
              </div>
            </div>

            <button type="submit" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 px-4 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-809">
              Sign In
              <ArrowRight className="h-4 w-4" data-id="element-810" />
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-2 text-sm" data-id="element-811">
            <span className="text-gray-500" data-id="element-812">Demo:</span>
            <button type="button" onClick={() => handleDemo('Maker')} className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50" data-id="element-813">
              Maker
            </button>
            <button type="button" onClick={() => handleDemo('Checker')} className="rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50" data-id="element-814">
              Checker
            </button>
          </div>

          <p className="mt-8 text-center text-xs text-gray-400" data-id="element-815">
            © 2026 Bunyaad Finance. All rights reserved.
          </p>
        </div>
      </div>
    </div>;
}